import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Phone, MapPin, HeartHandshake, Users, BookOpen, Megaphone, GraduationCap, Loader2, AlertCircle, Navigation, ExternalLink } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Badge, EmptyState, Alert, FormField } from '../../components/portal/ui'
import {
  listFacilities,
  nearbyFacilities,
  FACILITY_TYPE_LABELS,
  type Facility,
  type FacilityType,
} from '../../services/facilityService'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'

// Non-text structure for the options/volunteer cards. Text comes from i18n arrays.
const OPTION_ICONS = [Phone, MapPin, HeartHandshake, Users]
const OPTION_META = [
  { href: 'tel:1800116666' as string | undefined, to: undefined as string | undefined },
  { href: undefined, to: '/i-have-tb' },
  { href: undefined, to: '/support-a-patient#nikshay-mitra' },
  { href: undefined, to: '/groups' },
]
const VOLUNTEER_ICONS = [Megaphone, GraduationCap, BookOpen]

const TYPES = Object.keys(FACILITY_TYPE_LABELS) as FacilityType[]

export default function GetHelpPage() {
  const { t, i18n } = useTranslation()
  const [type, setType] = useState('')
  const [state, setState] = useState('')
  const [district, setDistrict] = useState('')
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Facility[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)
  const [geoStatus, setGeoStatus] = useState<string | null>(null)

  const options = t('pages.getHelp.options', { returnObjects: true }) as { title: string; desc: string; action: string }[]
  const volunteerItems = t('pages.getHelp.volunteer.items', { returnObjects: true }) as { title: string; desc: string }[]

  const search = async () => {
    setLoading(true); setError(null); setSearched(true)
    try {
      const data = await listFacilities({
        type: type || undefined, state: state.trim() || undefined,
        district: district.trim() || undefined, q: q.trim() || undefined, page: 0, size: 30,
      })
      setResults(data.content)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.getHelp.facility.noResultsDesc'))
    } finally { setLoading(false) }
  }

  const useMyLocation = () => {
    if (!navigator.geolocation) { setGeoStatus(t('pages.getHelp.facility.geoUnsupported')); return }
    setGeoStatus(t('pages.getHelp.facility.geoFinding')); setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const nearby = await nearbyFacilities(pos.coords.latitude, pos.coords.longitude, 50, 20)
          setResults(nearby); setSearched(true); setLoading(false)
          setGeoStatus(nearby.length > 0
            ? t('common.facilitiesNearby', { count: nearby.length })
            : t('pages.getHelp.facility.geoEmpty'))
        } catch (e: any) {
          setGeoStatus(null); setError(e?.response?.data?.message || t('pages.getHelp.facility.noResultsDesc'))
        }
      },
      () => setGeoStatus(t('pages.getHelp.facility.geoDenied')),
      { timeout: 10000 }
    )
  }

  const facilityTypeLabel = (val: FacilityType) =>
    t(`pages.getHelp.facility.facilityTypes.${val}`, { defaultValue: FACILITY_TYPE_LABELS[val] })

  return (
    <PortalLayout>
      <Seo
        title={t('pages.getHelp.seo.title')}
        description={t('pages.getHelp.seo.description')}
        canonicalPath="/get-help"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.getHelp', 'Get Help'), path: '/get-help' }]}
      />
      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.getHelp', 'Get Help') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.getHelp.hero.eyebrow')} title={t('pages.getHelp.hero.title')} description={t('pages.getHelp.hero.description')} />
          {/* A quiet, warm reassurance line — human story first. */}
          <p className="text-snow-700 text-pretty mt-2 max-w-2xl">
            {t('pages.getHelp.hero.reassurance')}
          </p>
          <Alert tone="warning" title={t('pages.getHelp.emergencyAlert.title')} className="mt-6">
            {t('pages.getHelp.emergencyAlert.body')}
          </Alert>
        </Container>
      </Section>

      <Section className="bg-white">
        <Container>
          <div className="grid sm:grid-cols-2 gap-5">
            {options.map((o, i) => {
              const Icon = OPTION_ICONS[i] ?? Phone
              const meta = OPTION_META[i]
              return (
                <Card key={i} hover className="p-6 flex gap-4 items-start">
                  <span className="shrink-0 w-12 h-12 rounded-xl bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
                    <Icon className="w-6 h-6" />
                  </span>
                  <div className="flex-1">
                    <h3 className="font-bold text-snow-900">{o.title}</h3>
                    <p className="text-sm text-snow-600 mt-1 mb-3">{o.desc}</p>
                    {meta.href ? (
                      <a href={meta.href} className="btn-primary btn-sm">{o.action}</a>
                    ) : (
                      <Button to={meta.to!} variant="primary" size="sm">{o.action}</Button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* Volunteer / NGO */}
      <Section className="bg-snow-50">
        <Container>
          <SectionHeading eyebrow={t('pages.getHelp.volunteer.eyebrow')} title={t('pages.getHelp.volunteer.title')} description={t('pages.getHelp.volunteer.description')} />
          <div className="grid sm:grid-cols-3 gap-5">
            {volunteerItems.map((v, i) => {
              const Icon = VOLUNTEER_ICONS[i] ?? Megaphone
              return (
                <Card key={i} className="p-6">
                  <Icon className="w-7 h-7 text-[rgb(0_152_186)] mb-3" aria-hidden="true" />
                  <h3 className="font-semibold text-snow-900">{v.title}</h3>
                  <p className="text-sm text-snow-600 mt-1">{v.desc}</p>
                </Card>
              )
            })}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button to="/contact" variant="primary">{t('pages.getHelp.volunteer.ctaRegister')}</Button>
            <Button to="/campaigns" variant="outline">{t('pages.getHelp.volunteer.ctaCampaigns')}</Button>
            <Button to="/resources" variant="outline">{t('pages.getHelp.volunteer.ctaResources')}</Button>
          </div>
        </Container>
      </Section>

      {/* Facility finder */}
      <Section className="bg-white">
        <Container>
          <Card className="p-6">
            <h2 className="font-bold text-snow-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-[rgb(15_76_129)]" aria-hidden="true" /> {t('pages.getHelp.facility.title')}</h2>
            <p className="text-sm text-snow-600 mt-2">
              {t('pages.getHelp.facility.intro')}
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <FormField label={t('pages.getHelp.facility.searchLabel')} htmlFor="fac-q">
                <input id="fac-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('pages.getHelp.facility.searchPlaceholder')} className="input" onKeyDown={(e) => e.key === 'Enter' && search()} />
              </FormField>
              <FormField label={t('pages.getHelp.facility.stateLabel')} htmlFor="fac-state">
                <input id="fac-state" type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder={t('pages.getHelp.facility.statePlaceholder')} className="input" onKeyDown={(e) => e.key === 'Enter' && search()} />
              </FormField>
              <FormField label={t('pages.getHelp.facility.districtLabel')} htmlFor="fac-dist">
                <input id="fac-dist" type="text" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder={t('pages.getHelp.facility.districtPlaceholder')} className="input" onKeyDown={(e) => e.key === 'Enter' && search()} />
              </FormField>
              <FormField label={t('pages.getHelp.facility.typeLabel')} htmlFor="fac-type">
                <select id="fac-type" value={type} onChange={(e) => setType(e.target.value)} className="select">
                  <option value="">{t('pages.getHelp.facility.allTypes')}</option>
                  {TYPES.map((t2) => <option key={t2} value={t2}>{facilityTypeLabel(t2)}</option>)}
                </select>
              </FormField>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <Button onClick={search} variant="primary" size="sm" icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined} disabled={loading}>
                {loading ? t('pages.getHelp.facility.searching') : t('pages.getHelp.facility.searchBtn')}
              </Button>
              <Button onClick={useMyLocation} variant="outline" size="sm" icon={<Navigation className="w-4 h-4" />}>{t('pages.getHelp.facility.useLocation')}</Button>
            </div>

            {geoStatus && (
              <div className="mt-4 text-sm text-snow-600 bg-snow-50 p-3 rounded-lg" role="status">{geoStatus}</div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 mt-4 bg-red-50 text-red-700 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {error}</div>
            )}

            {searched && !loading && !error && (
              <div className="mt-6">
                {results.length === 0 ? (
                  <EmptyState
                    icon={<MapPin className="w-7 h-7" />}
                    title={t('pages.getHelp.facility.noResultsTitle')}
                    description={t('pages.getHelp.facility.noResultsDesc')}
                    action={<a href="tel:1800116666" className="btn-primary btn-sm">{t('pages.getHelp.facility.callBtn')}</a>}
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-snow-500" aria-live="polite">{t('common.facilitiesFound', { count: results.length })}</p>
                    {results.some((f) => f.matchedLanguage && f.matchedLanguage !== (i18n.language?.split('-')[0] || 'en')) && (
                      <TranslatedContentNotice matchedLanguage="en" />
                    )}
                    {results.map((f) => (
                      <Card key={f.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                        <span className="shrink-0 w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true"><MapPin className="w-5 h-5" /></span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-snow-900">{f.name}</h4>
                            <Badge tone="slate">{facilityTypeLabel(f.facilityType)}</Badge>
                            {f.distanceKm != null && <Badge tone="teal">{t('pages.getHelp.facility.distanceBadge', { count: f.distanceKm })}</Badge>}
                          </div>
                          {f.address && <p className="text-sm text-snow-600 mt-1">{f.address}</p>}
                          <p className="text-xs text-snow-500 mt-1">
                            {[f.district, f.state, f.pincode].filter(Boolean).join(', ')}
                            {f.phone && ` · ${f.phone}`}
                          </p>
                          {f.servicesOffered && <p className="text-xs text-snow-500 mt-1">{t('pages.getHelp.facility.services')} {f.servicesOffered}</p>}
                          {f.operatingHours && <p className="text-xs text-snow-500">{t('pages.getHelp.facility.hours')} {f.operatingHours}</p>}
                        </div>
                        {f.latitude != null && f.longitude != null && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${f.latitude},${f.longitude}`} target="_blank" rel="noopener noreferrer" className="btn-outline btn-sm shrink-0 inline-flex items-center gap-1">
                            {t('pages.getHelp.facility.directions')} <ExternalLink className="w-3 h-3" aria-hidden="true" />
                          </a>
                        )}
                        {f.phone && (
                          <a href={`tel:${f.phone}`} className="btn-outline btn-sm shrink-0">{t('pages.getHelp.facility.call')}</a>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Alert tone="info" title={t('pages.getHelp.facility.directoryAlertTitle')}>
              {t('pages.getHelp.facility.directoryAlertBody')}
            </Alert>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}